import { List, ListItem, Text } from '@mezzoforte/forge';
import { ReactNode, useContext } from 'react';
import InfoTooltip from '@/components/InfoTooltip/InfoTooltip';
import { BiddingContext } from '@/context/BiddingContext';
import { Delivery } from '@/types/ApiResponse';
import { formatCurrency } from '@/util/currency';

interface DeliveryInfoProps {
  delivery: Delivery | null;
}

export function DeliveryInfo({ delivery }: DeliveryInfoProps) {
  const { processingCostInfo } = useContext(BiddingContext);

  if (!delivery?.selectableMethods) {
    return null;
  }

  const { deliveryAvailable, deliveryExpenses, deliveryExpensesAvailable, retrievalAvailable, retrievalExpenses } =
    delivery;

  const deliveryInfo: { key: string; content: ReactNode }[] = (() => {
    const delivery = () => {
      if (!deliveryAvailable) return 'Toimitus ei mahdollinen';
      if (!deliveryExpensesAvailable) return null; // Dependent on buyers location
      return (
        <>
          Toimitus{' '}
          <Text
            variant="bold"
            as="span"
            fontSize="inherit"
          >
            {formatCurrency(deliveryExpenses ?? 0)}
          </Text>{' '}
          (sis. pakkaus- ja lähetyskulut)
        </>
      );
    };

    const retrieval = () => {
      if (retrievalAvailable)
        return (
          <>
            Nouto{' '}
            <Text
              variant="bold"
              as="span"
              fontSize="inherit"
            >
              {formatCurrency(retrievalExpenses ?? 0)}
            </Text>
          </>
        );
      return 'Nouto ei mahdollinen';
    };

    const deliveryOffer = () => {
      if (!deliveryAvailable || deliveryExpensesAvailable || retrievalExpenses) return null;
      return 'Voit pyytää ilmoittajalta toimitustarjouksen';
    };

    const processingCost = () => {
      if (!processingCostInfo) return null;
      return (
        <>
          {processingCostInfo.title}{' '}
          <Text
            as="span"
            variant="bold"
            fontSize="inherit"
          >
            {formatCurrency(processingCostInfo.sum)}
          </Text>
          <InfoTooltip label="Maksetaan Huutokaupat.comille kohteen maksamisen yhteydessä." />
        </>
      );
    };

    return [
      { key: 'delivery', content: delivery() },
      { key: 'retrieval', content: retrieval() },
      { key: 'delivery-offer', content: deliveryOffer() },
      { key: 'processing-cost', content: processingCost() },
    ].filter((c) => c.content !== null);
  })();

  return (
    <>
      <Text
        variant="bodySmallBold"
        mb={1}
        mt={3}
      >
        Toimitustavat ja kulut
      </Text>
      <List fontSize={14}>
        {deliveryInfo.map((row) => (
          <ListItem
            fontSize="inherit"
            key={row.key}
          >
            {row.content}
          </ListItem>
        ))}
      </List>

      {deliveryAvailable && deliveryExpensesAvailable && (
        <Text
          variant="subdued"
          fontSize={14}
        >
          Toimituskulut koskevat toimitusta Manner-Suomeen.
        </Text>
      )}
    </>
  );
}
